import { isValidPublicId, resolveWirePublicId } from '../lib/publicId'
import { apiRequest } from './client'

export type CreationStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED'

export type CreationResult = {
  requestId: string
  platform: string
  mode: string
  model: string
  text: string | null
  imageUrls: string[]
  storagePublicIds: string[]
  metadata: Record<string, unknown>
  createdAt: string
}

export type CreationTask = {
  publicId: string
  platform: string
  mode: string
  prompt: string
  model: string | null
  referenceImages: string[]
  options: Record<string, unknown>
  status: CreationStatus
  result: CreationResult | null
  /** 稳定错误码，见 @glyphic/shared-core CreationTaskErrorCode */
  errorCode?: string | null
  errorMessage: string | null
  /** 是否可补偿落库（后端 CreationReconcilePolicy 判定） */
  reconcilable?: boolean
  createdAt: string
  updatedAt: string
}

type CreationTaskWire = CreationTask & {
  id?: string
  public_id?: string
}

export function normalizeCreationTask(task: CreationTaskWire): CreationTask {
  return {
    ...task,
    publicId: resolveWirePublicId(task, 'publicId', 'public_id', 'id'),
  }
}

export function isValidCreationTaskPublicId(value: string | null | undefined): boolean {
  return isValidPublicId(value)
}

export type CreateCreationPayload = {
  platform?: string
  mode: 'TEXT' | 'TEXT_TO_IMAGE' | 'IMAGE_TO_IMAGE'
  prompt: string
  model?: string
  referenceImages?: string[]
  options?: Record<string, unknown>
  /** 为 true 时生成成功后对每张输出图 URL 做多米超清（单张失败则保留原图） */
  superResolution?: boolean
}

export type CreateCreationOptions = {
  /**
   * 幂等键：同一生成意图（含超时后重试）必须复用同一个键。
   * 后端按「用户 + 幂等键」去重，重复提交直接接回原任务，
   * 避免重复生成与重复扣费。
   */
  idempotencyKey?: string
}

export function createCreation(
  payload: CreateCreationPayload,
  options: CreateCreationOptions = {},
) {
  const headers: Record<string, string> = {}
  const idempotencyKey = options.idempotencyKey?.trim()
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey
  }
  return apiRequest<CreationTaskWire>('/v1/creations', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  }).then(normalizeCreationTask)
}

export function getCreation(publicId: string) {
  return apiRequest<CreationTaskWire>(`/v1/creations/${encodeURIComponent(publicId)}`, {
    method: 'GET',
  }).then(normalizeCreationTask)
}

/** 补偿落库：失败任务在仍有上游结果时重新入队，仅重试存储。 */
export function reconcileCreation(publicId: string) {
  return apiRequest<CreationTaskWire>(`/v1/creations/${encodeURIComponent(publicId)}/reconcile`, {
    method: 'POST',
  }).then(normalizeCreationTask)
}
